<template>
  <v-container fluid class="pa-4">
    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="4000" location="top right">
      {{ snackbarText }}
      <template v-slot:actions>
        <v-btn icon="mdi-close" variant="text" @click="snackbar = false" />
      </template>
    </v-snackbar>

    <!-- Header -->
    <div class="d-flex align-center mb-5" style="gap: 12px;">
      <v-avatar color="grey-darken-3" size="40" rounded="lg">
        <v-icon color="white">mdi-email-edit-outline</v-icon>
      </v-avatar>
      <div>
        <div class="text-h6 font-weight-bold">Invitation Email</div>
        <div class="text-caption text-medium-emphasis">Customize the email sent to attendees when they are registered</div>
      </div>
      <v-spacer />
      <v-btn variant="outlined" color="grey" rounded="lg" prepend-icon="mdi-refresh" class="mr-2" @click="resetToDefault">
        Reset
      </v-btn>
      <v-btn color="grey-darken-3" rounded="lg" prepend-icon="mdi-content-save" :loading="saving" @click="save">
        Save
      </v-btn>
    </div>

    <v-row>
      <!-- ── Form column ─────────────────────────── -->
      <v-col cols="12" lg="5">

        <!-- Subject -->
        <v-card variant="outlined" rounded="xl" class="pa-4 mb-4">
          <div class="text-subtitle-2 font-weight-bold mb-3 d-flex align-center" style="gap: 6px;">
            <v-icon size="18" color="grey-darken-3">mdi-format-title</v-icon>
            Email Subject
          </div>
          <v-text-field
            v-model="subject"
            label="What attendees see as the email subject"
            variant="outlined"
            density="comfortable"
            hide-details
          />
          <div class="mt-2 d-flex flex-wrap" style="gap: 6px;">
            <span class="text-caption text-medium-emphasis align-self-center">Insert:</span>
            <v-chip
              v-for="v in vars"
              :key="v.tag"
              size="x-small"
              variant="tonal"
              color="grey-darken-3"
              @click="insertInto('subject', v.tag)"
              style="cursor:pointer;"
            >
              {{ v.label }}
            </v-chip>
          </div>
        </v-card>

        <!-- Branding -->
        <v-card variant="outlined" rounded="xl" class="pa-4 mb-4">
          <div class="text-subtitle-2 font-weight-bold mb-3 d-flex align-center" style="gap: 6px;">
            <v-icon size="18" color="grey-darken-3">mdi-palette-outline</v-icon>
            Branding
          </div>
          <v-text-field
            v-model="settings.headerTitle"
            label="Organisation / event name shown at the top"
            variant="outlined"
            density="comfortable"
            hide-details
            class="mb-3"
          />
          <div class="text-caption text-medium-emphasis mb-2">Accent colour</div>
          <div class="d-flex flex-wrap" style="gap: 10px;">
            <div
              v-for="color in colorPresets"
              :key="color"
              @click="settings.accentColor = color"
              :style="{
                background: color,
                width: '32px', height: '32px',
                borderRadius: '8px',
                cursor: 'pointer',
                border: settings.accentColor === color ? '3px solid #fff' : '3px solid transparent',
                outline: settings.accentColor === color ? '2px solid ' + color : 'none',
                boxSizing: 'border-box',
              }"
            />
            <!-- Custom colour input -->
            <div style="position:relative; width:32px; height:32px;">
              <div
                :style="{
                  background: isCustomColor ? settings.accentColor : '#e0e0e0',
                  width: '32px', height: '32px', borderRadius: '8px',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  border: isCustomColor ? '3px solid #fff' : '3px solid transparent',
                  outline: isCustomColor ? '2px solid ' + settings.accentColor : 'none',
                  boxSizing: 'border-box',
                }"
              >
                <v-icon size="16" :color="isCustomColor ? 'white' : 'grey'">mdi-eyedropper</v-icon>
              </div>
              <input
                type="color"
                v-model="settings.accentColor"
                style="position:absolute;top:0;left:0;width:32px;height:32px;opacity:0;cursor:pointer;"
              />
            </div>
          </div>
        </v-card>

        <!-- Message content -->
        <v-card variant="outlined" rounded="xl" class="pa-4 mb-4">
          <div class="text-subtitle-2 font-weight-bold mb-3 d-flex align-center" style="gap: 6px;">
            <v-icon size="18" color="grey-darken-3">mdi-message-text-outline</v-icon>
            Email Content
          </div>

          <v-text-field
            ref="greetingField"
            v-model="settings.greeting"
            label="Greeting line"
            variant="outlined"
            density="comfortable"
            hide-details
            class="mb-2"
          />
          <div class="mb-3 d-flex flex-wrap" style="gap: 6px;">
            <span class="text-caption text-medium-emphasis align-self-center">Insert:</span>
            <v-chip v-for="v in vars" :key="v.tag" size="x-small" variant="tonal" color="grey-darken-3"
              @click="insertInto('greeting', v.tag)" style="cursor:pointer;">
              {{ v.label }}
            </v-chip>
          </div>

          <v-textarea
            ref="messageField"
            v-model="settings.message"
            label="Main message"
            variant="outlined"
            density="comfortable"
            rows="4"
            hide-details
            class="mb-2"
          />
          <div class="mb-3 d-flex flex-wrap" style="gap: 6px;">
            <span class="text-caption text-medium-emphasis align-self-center">Insert:</span>
            <v-chip v-for="v in vars" :key="v.tag" size="x-small" variant="tonal" color="grey-darken-3"
              @click="insertInto('message', v.tag)" style="cursor:pointer;">
              {{ v.label }}
            </v-chip>
          </div>

          <v-switch
            v-model="settings.showDetails"
            label="Show attendee details table (name, email, event)"
            color="grey-darken-3"
            density="compact"
            hide-details
            class="mb-3"
          />

          <v-text-field
            v-model="settings.closing"
            label="Closing line (e.g. We look forward to welcoming you!)"
            variant="outlined"
            density="comfortable"
            hide-details
            class="mb-2"
          />
          <v-text-field
            v-model="settings.signature"
            label="Signature (e.g. The Sunrise Events Team)"
            variant="outlined"
            density="comfortable"
            hide-details
          />
        </v-card>

      </v-col>

      <!-- ── Preview column ──────────────────────── -->
      <v-col cols="12" lg="7">
        <v-card variant="outlined" rounded="xl" style="position: sticky; top: 16px;">
          <div class="pa-4 pb-2 d-flex align-center" style="gap: 10px;">
            <v-icon color="grey-darken-3">mdi-eye-outline</v-icon>
            <div>
              <div class="text-subtitle-2 font-weight-bold">Live Preview</div>
              <div class="text-caption text-medium-emphasis">Exactly how the email will look</div>
            </div>
            <v-spacer />
            <v-chip size="small" color="blue" variant="tonal" prepend-icon="mdi-flask-outline">
              Sample data
            </v-chip>
          </div>

          <!-- Subject bar -->
          <v-divider />
          <div class="px-4 py-2 d-flex align-center" style="background:#fafafa; gap: 8px;">
            <span class="text-caption text-medium-emphasis" style="min-width:56px; flex-shrink:0;">Subject:</span>
            <span class="text-body-2 font-weight-medium">{{ previewSubject }}</span>
          </div>
          <v-divider />

          <!-- Email body preview -->
          <div class="pa-3" style="background: #e8e8e8; min-height: 480px; overflow-y: auto;">
            <div v-html="previewHtml" style="pointer-events:none; max-width:620px; margin:auto;" />
          </div>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import axios from "axios";

const SAMPLE = {
  name:       "Amira Benali",
  event_name: "Annual Gala 2026",
  email:      "amira.benali@example.com",
  year:       new Date().getFullYear(),
};

const COLOR_PRESETS = [
  "#212121", "#1565C0", "#2E7D32", "#C62828",
  "#6A1B9A", "#00695C", "#E65100", "#37474F",
];

const defaultSettings = () => ({
  headerTitle: "Sunrise Events",
  accentColor: "#212121",
  greeting:    "Hello {{name}}! 🎉",
  message:     "We are thrilled to confirm your registration for {{event_name}}.\n\nYour invitation is attached to this email. Please bring it (printed or on your phone) to present at the entrance.",
  showDetails: true,
  closing:     "We look forward to welcoming you!",
  signature:   "The Sunrise Events Team",
});

export default {
  data() {
    return {
      saving:   false,
      snackbar: false,
      snackbarText:  "",
      snackbarColor: "success",
      subject:  "🎫 Your invitation to {{event_name}} is confirmed!",
      settings: defaultSettings(),
      colorPresets: COLOR_PRESETS,
      vars: [
        { tag: "{{name}}",       label: "Attendee Name"  },
        { tag: "{{event_name}}", label: "Event Name"      },
        { tag: "{{email}}",      label: "Attendee Email"  },
        { tag: "{{year}}",       label: "Current Year"    },
      ],
    };
  },

  computed: {
    isCustomColor() {
      return !COLOR_PRESETS.includes(this.settings.accentColor);
    },
    previewSubject() {
      return this.fill(this.subject);
    },
    previewHtml() {
      return this.buildHtml(this.settings, true);
    },
  },

  mounted() {
    this.load();
  },

  methods: {
    notify(text, color = "success") {
      this.snackbarText  = text;
      this.snackbarColor = color;
      this.snackbar      = true;
    },

    fill(str, sample = SAMPLE) {
      return (str || "")
        .replace(/\{\{name\}\}/g,       sample.name)
        .replace(/\{\{event_name\}\}/g, sample.event_name)
        .replace(/\{\{email\}\}/g,      sample.email)
        .replace(/\{\{year\}\}/g,       sample.year);
    },

    buildHtml(s, forPreview = false) {
      const accent    = s.accentColor || "#212121";
      const title     = s.headerTitle || "Sunrise Events";
      const greeting  = forPreview ? this.fill(s.greeting || "").replace(/\n/g, "<br/>")
                                   : (s.greeting || "").replace(/\n/g, "<br/>");
      const message   = forPreview ? this.fill(s.message || "").replace(/\n/g, "<br/>")
                                   : (s.message || "").replace(/\n/g, "<br/>");
      const closing   = forPreview ? this.fill(s.closing || "").replace(/\n/g, "<br/>")
                                   : (s.closing || "").replace(/\n/g, "<br/>");
      const signature = s.signature || "The Sunrise Events Team";
      const year      = forPreview ? SAMPLE.year : "{{year}}";

      const details = s.showDetails !== false
        ? `<div style="background:#f9f9f9;border-left:4px solid ${accent};padding:16px 20px;border-radius:0 6px 6px 0;margin:24px 0;">
            <table style="border-collapse:collapse;width:100%;font-size:14px;color:#555;">
              <tr><td style="padding:4px 0;width:90px;color:#888;">Name</td>
                  <td style="padding:4px 0;font-weight:bold;color:${accent};">${forPreview ? SAMPLE.name : "{{name}}"}</td></tr>
              <tr><td style="padding:4px 0;color:#888;">Email</td>
                  <td style="padding:4px 0;font-weight:bold;color:${accent};">${forPreview ? SAMPLE.email : "{{email}}"}</td></tr>
              <tr><td style="padding:4px 0;color:#888;">Event</td>
                  <td style="padding:4px 0;font-weight:bold;color:${accent};">${forPreview ? SAMPLE.event_name : "{{event_name}}"}</td></tr>
            </table>
          </div>`
        : "";

      return `<div style="font-family:Arial,sans-serif;max-width:620px;margin:auto;background:#f5f5f5;padding:24px;border-radius:10px;">
  <div style="background:${accent};padding:24px 32px;border-radius:10px 10px 0 0;text-align:center;">
    <h1 style="color:#fff;margin:0;font-size:26px;letter-spacing:1px;">${title}</h1>
  </div>
  <div style="background:#fff;padding:32px;border-radius:0 0 10px 10px;">
    <h2 style="color:#212121;margin-top:0;">${greeting}</h2>
    <p style="color:#444;line-height:1.7;font-size:15px;">${message}</p>
    ${details}
    ${closing ? `<p style="color:#444;line-height:1.7;font-size:15px;">${closing}</p>` : ""}
    <p style="color:#444;font-size:15px;margin-top:24px;">Warm regards,<br/>
      <strong style="color:${accent};">${signature}</strong></p>
  </div>
  <p style="text-align:center;color:#aaa;font-size:12px;margin-top:16px;">
    &copy; ${year} ${title} &mdash; This is an automated message, please do not reply.
  </p>
</div>`;
    },

    broadcastColor(color) {
      localStorage.setItem("brandColor", color);
      window.dispatchEvent(new CustomEvent("brandColorChanged", { detail: color }));
    },

    async load() {
      try {
        const { data } = await axios.get("/api/email-template");
        if (data.subject)  this.subject  = data.subject;
        if (data.settings) this.settings = { ...defaultSettings(), ...data.settings };
        this.broadcastColor(this.settings.accentColor);
      } catch {
        this.notify("Could not load saved template — using defaults.", "orange");
      }
    },

    async save() {
      this.saving = true;
      try {
        await axios.put("/api/email-template", {
          subject:  this.subject,
          settings: this.settings,
        });
        this.broadcastColor(this.settings.accentColor);
        this.notify("Email template saved successfully.");
      } catch (e) {
        this.notify(e.response?.data?.error || "Save failed.", "red");
      } finally {
        this.saving = false;
      }
    },

    resetToDefault() {
      if (!confirm("Reset to the default template?")) return;
      this.subject  = "🎫 Your invitation to {{event_name}} is confirmed!";
      this.settings = defaultSettings();
      this.notify("Reset to default — click Save to keep it.", "blue");
    },

    insertInto(field, tag) {
      // field is 'subject' | 'greeting' | 'message'
      if (field === "subject") {
        this.subject += tag;
        return;
      }
      // For v-textarea / v-text-field, get the native input/textarea element
      const refName = field === "greeting" ? "greetingField" : "messageField";
      const el = this.$refs[refName]?.$el?.querySelector("input,textarea");
      if (!el) {
        this.settings[field] += tag;
        return;
      }
      const start  = el.selectionStart;
      const end    = el.selectionEnd;
      const before = (this.settings[field] || "").substring(0, start);
      const after  = (this.settings[field] || "").substring(end);
      this.settings[field] = before + tag + after;
      this.$nextTick(() => {
        el.selectionStart = el.selectionEnd = start + tag.length;
        el.focus();
      });
    },
  },
};
</script>
